// Settings → General (and Menu → Deals → Upsells): the switch for upsell
// suggestions (F-DEAL-N4). app_settings 'upsell.config' is
// {enabled, till, online, deal_upgrade}; off by default. The server accepts
// only those four booleans and lets admins and managers write it.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'
import type { UpsellConfig } from '@/types/deals'

export const UPSELL_SETTING_KEY = 'upsell.config'

export const DEFAULT_UPSELL_CONFIG: UpsellConfig = { enabled: false, till: true, online: true, deal_upgrade: true }

/** Reads a stored value (object or JSON string) with defaults for missing keys. */
export function upsellConfigFrom(raw: unknown): UpsellConfig {
  let v: unknown = raw
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v)
    } catch {
      v = null
    }
  }
  const o = v && typeof v === 'object' ? (v as Partial<Record<keyof UpsellConfig, unknown>>) : {}
  const b = (x: unknown, d: boolean) => (typeof x === 'boolean' ? x : d)
  return {
    enabled: b(o.enabled, DEFAULT_UPSELL_CONFIG.enabled),
    till: b(o.till, DEFAULT_UPSELL_CONFIG.till),
    online: b(o.online, DEFAULT_UPSELL_CONFIG.online),
    deal_upgrade: b(o.deal_upgrade, DEFAULT_UPSELL_CONFIG.deal_upgrade),
  }
}

export function UpsellSettingCard() {
  const all = useAllSettings()
  const stored = all?.[UPSELL_SETTING_KEY]
  const qc = useQueryClient()
  const { toast } = useToast()
  const [value, setValue] = useState<UpsellConfig>(() => upsellConfigFrom(stored))
  useEffect(() => setValue(upsellConfigFrom(stored)), [stored])

  const save = useMutation({
    mutationFn: async (next: UpsellConfig) => {
      const res = await apiClient.updateSetting(UPSELL_SETTING_KEY, next)
      if (!res.success) throw new Error(res.message || 'Could not save')
      return res
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      qc.invalidateQueries({ queryKey: ['admin-upsells'] })
      qc.invalidateQueries({ queryKey: ['menu-rules'] })
      toast({ title: 'Settings saved', description: 'Upsell suggestions updated.' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })

  const role = getStoredUserRole()
  if (role !== 'admin' && role !== 'manager') return null

  const current = upsellConfigFrom(stored)
  const dirty =
    value.enabled !== current.enabled ||
    value.till !== current.till ||
    value.online !== current.online ||
    value.deal_upgrade !== current.deal_upgrade

  const sub: Array<{ key: Exclude<keyof UpsellConfig, 'enabled'>; label: string; help: string }> = [
    { key: 'till', label: 'On the till & Server tablet', help: 'Staff see a suggestion when they add an item.' },
    { key: 'online', label: 'On the online store', help: 'Guests see “Add a drink?” style suggestions in their cart.' },
    {
      key: 'deal_upgrade',
      label: 'Suggest upgrading to a deal',
      help: 'For example Zinger Burger → Zinger Deal, when the item is the main part of a deal.',
    },
  ]

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Upsell suggestions</CardTitle>
        <CardDescription>
          Suggest an extra item or a deal when something is added to an order. Pick what to suggest in Menu → Deals → Upsells, or on an item.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start gap-3">
          <Switch id="upsell-enabled" checked={value.enabled} onCheckedChange={(v) => setValue((p) => ({ ...p, enabled: v }))} />
          <div className="space-y-0.5">
            <Label htmlFor="upsell-enabled" className="text-base">
              Show upsell suggestions
            </Label>
            <p className="text-sm text-muted-foreground">Off by default. Nothing is added to a bill without someone tapping it.</p>
          </div>
        </div>
        {value.enabled &&
          sub.map((s) => (
            <div key={s.key} className="flex items-start gap-3 pl-6">
              <Switch id={`upsell-${s.key}`} checked={value[s.key]} onCheckedChange={(v) => setValue((p) => ({ ...p, [s.key]: v }))} />
              <div className="space-y-0.5">
                <Label htmlFor={`upsell-${s.key}`} className="text-base">
                  {s.label}
                </Label>
                <p className="text-sm text-muted-foreground">{s.help}</p>
              </div>
            </div>
          ))}
        <Button className="h-11 px-6" disabled={save.isPending || !dirty} onClick={() => save.mutate(value)}>
          Save
        </Button>
      </CardContent>
    </Card>
  )
}
